// RabattcodeTab.jsx — Verwaltung von Rabattcodes für ein Turnier
import React, { useState, useEffect, useCallback } from 'react';

const GUELTIG_OPTIONEN = [
  { value: 'alle',    label: 'Alle Teilnehmer' },
  { value: 'verein',  label: 'Bestimmter Verein' },
  { value: 'person',  label: 'Bestimmte Person' },
];

const emptyCode = {
  code: '',
  bezeichnung: '',
  rabatt_betrag: '',
  rabatt_typ: 'prozent',   // 'prozent' | 'euro'
  gueltig_fuer: 'alle',
  verein_id: '',
  wettkaempfer_id: '',
  max_nutzungen: '',
};

export default function RabattcodeTab({ turnierId, authHeader }) {
  const [codes, setCodes]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [vereine, setVereine]           = useState([]);
  const [wettkaempfer, setWettkaempfer] = useState([]);
  const [showForm, setShowForm]         = useState(false);
  const [formData, setFormData]         = useState(emptyCode);
  const [editId, setEditId]             = useState(null);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');

  const load = useCallback(async () => {
    if (!turnierId) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/turniere/${turnierId}/rabattcodes`, { headers: authHeader });
      const d   = await res.json();
      setCodes(d.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [turnierId, authHeader]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Promise.all([
      fetch('/api/vereine',      { headers: authHeader }).then(r => r.json()),
      fetch('/api/wettkaempfer', { headers: authHeader }).then(r => r.json()),
    ]).then(([vd, wd]) => {
      setVereine(Array.isArray(vd) ? vd : (vd.data || []));
      setWettkaempfer(Array.isArray(wd) ? wd : (wd.data || []));
    }).catch(console.error);
  }, [authHeader]);

  const setField = (key, val) => setFormData(f => ({ ...f, [key]: val }));

  const openAdd = () => {
    setFormData(emptyCode);
    setEditId(null);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const openEdit = (code) => {
    setFormData({
      code:           code.code,
      bezeichnung:    code.bezeichnung || '',
      rabatt_betrag:  code.rabatt_prozent,           // DB-Feld heißt noch rabatt_prozent
      rabatt_typ:     code.rabatt_typ || 'prozent',
      gueltig_fuer:   code.gueltig_fuer,
      verein_id:      code.verein_id   || '',
      wettkaempfer_id:code.wettkaempfer_id || '',
      max_nutzungen:  code.max_nutzungen || '',
    });
    setEditId(code.id);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    if (!formData.code.trim())    return setError('Code darf nicht leer sein.');
    if (!formData.rabatt_betrag)  return setError('Rabattbetrag ist erforderlich.');

    setSaving(true);
    setError('');
    try {
      const body = {
        code:           formData.code,
        bezeichnung:    formData.bezeichnung || null,
        rabatt_prozent: parseFloat(formData.rabatt_betrag),   // Backend-Spaltenname bleibt
        rabatt_typ:     formData.rabatt_typ,
        gueltig_fuer:   formData.gueltig_fuer,
        verein_id:      formData.gueltig_fuer === 'verein' ? (formData.verein_id || null) : null,
        wettkaempfer_id:formData.gueltig_fuer === 'person' ? (formData.wettkaempfer_id || null) : null,
        max_nutzungen:  formData.max_nutzungen ? parseInt(formData.max_nutzungen) : null,
      };

      const url    = editId
        ? `/api/turniere/${turnierId}/rabattcodes/${editId}`
        : `/api/turniere/${turnierId}/rabattcodes`;
      const method = editId ? 'PUT' : 'POST';

      const res = await fetch(url, { method, headers: authHeader, body: JSON.stringify(body) });
      const d   = await res.json();
      if (!res.ok) throw new Error(d.error || 'Fehler beim Speichern');

      setSuccess(editId ? 'Code aktualisiert.' : 'Code erstellt.');
      setShowForm(false);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    await fetch(`/api/turniere/${turnierId}/rabattcodes/${id}/toggle`, {
      method: 'PATCH', headers: authHeader,
    });
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Code wirklich löschen?')) return;
    await fetch(`/api/turniere/${turnierId}/rabattcodes/${id}`, {
      method: 'DELETE', headers: authHeader,
    });
    load();
  };

  const formatGueltigFuer = (code) => {
    if (code.gueltig_fuer === 'verein' && code.verein_name) return `${code.verein_name}`;
    if (code.gueltig_fuer === 'person' && code.wettkaempfer_name) return `${code.wettkaempfer_name}`;
    return 'Alle';
  };

  const formatRabatt = (code) => {
    const typ = code.rabatt_typ || 'prozent';
    return typ === 'euro'
      ? `${parseFloat(code.rabatt_prozent).toFixed(2)} €`
      : `${parseFloat(code.rabatt_prozent)} %`;
  };

  return (
    <div className="tbe-section">
      <div className="tbe-codes-header">
        <div>
          <div className="tbe-card-title" style={{ fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.2rem' }}>
            Rabattcodes
          </div>
          <div className="tbe-hint">Individuelle Codes mit % oder €-Rabatt — für alle, einen Verein oder eine Person.</div>
        </div>
        <button type="button" className="tbe-btn-primary" onClick={openAdd}>+ Code hinzufügen</button>
      </div>

      {success && <div className="tbe-msg tbe-msg--ok">{success}</div>}

      {/* Formular */}
      {showForm && (
        <div className="tbe-card" style={{ marginBottom: '1rem' }}>
          <div className="tbe-card-title">{editId ? 'Code bearbeiten' : 'Neuer Code'}</div>
          {error && <div className="tbe-msg tbe-msg--err">{error}</div>}
          <div className="tbe-grid">
            <div className="tbe-field">
              <label>Code *</label>
              <input
                type="text"
                value={formData.code}
                onChange={e => setField('code', e.target.value.toUpperCase())}
                placeholder="z.B. SOMMER25"
                style={{ textTransform: 'uppercase', letterSpacing: '0.1em' }}
              />
              <span className="tbe-hint">Groß-/Kleinschreibung egal</span>
            </div>
            <div className="tbe-field">
              <label>Bezeichnung</label>
              <input
                type="text"
                value={formData.bezeichnung}
                onChange={e => setField('bezeichnung', e.target.value)}
                placeholder="z.B. Sommer-Rabatt"
              />
            </div>

            {/* Rabatt Betrag + Typ */}
            <div className="tbe-field">
              <label>Rabatt *</label>
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                <input
                  type="number"
                  min="0.01"
                  step={formData.rabatt_typ === 'prozent' ? '0.5' : '0.50'}
                  max={formData.rabatt_typ === 'prozent' ? '100' : undefined}
                  value={formData.rabatt_betrag}
                  onChange={e => setField('rabatt_betrag', e.target.value)}
                  placeholder={formData.rabatt_typ === 'prozent' ? 'z.B. 10' : 'z.B. 5.00'}
                  style={{ flex: 1 }}
                />
                <div className="tbe-mode-toggle">
                  <button
                    type="button"
                    className={`tbe-mode-btn${formData.rabatt_typ === 'prozent' ? ' tbe-mode-btn--active' : ''}`}
                    onClick={() => setField('rabatt_typ', 'prozent')}
                  >%</button>
                  <button
                    type="button"
                    className={`tbe-mode-btn${formData.rabatt_typ === 'euro' ? ' tbe-mode-btn--active' : ''}`}
                    onClick={() => setField('rabatt_typ', 'euro')}
                  >€</button>
                </div>
              </div>
            </div>

            <div className="tbe-field">
              <label>Max. Nutzungen</label>
              <input
                type="number"
                min="1"
                value={formData.max_nutzungen}
                onChange={e => setField('max_nutzungen', e.target.value)}
                placeholder="Leer = unbegrenzt"
              />
            </div>
            <div className="tbe-field">
              <label>Gültig für</label>
              <select value={formData.gueltig_fuer} onChange={e => setField('gueltig_fuer', e.target.value)}>
                {GUELTIG_OPTIONEN.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {formData.gueltig_fuer === 'verein' && (
              <div className="tbe-field">
                <label>Verein</label>
                <select value={formData.verein_id} onChange={e => setField('verein_id', e.target.value)}>
                  <option value="">— Verein wählen —</option>
                  {vereine.map(v => (
                    <option key={v.vereins_id} value={v.vereins_id}>{v.name}</option>
                  ))}
                </select>
              </div>
            )}
            {formData.gueltig_fuer === 'person' && (
              <div className="tbe-field">
                <label>Person</label>
                <select value={formData.wettkaempfer_id} onChange={e => setField('wettkaempfer_id', e.target.value)}>
                  <option value="">— Person wählen —</option>
                  {wettkaempfer.map(w => (
                    <option key={w.wettkaempfer_id} value={w.wettkaempfer_id}>{w.vorname} {w.nachname}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
            <button type="button" className="tbe-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Speichern…' : editId ? 'Aktualisieren' : 'Erstellen'}
            </button>
            <button type="button" className="tbe-btn-cancel" onClick={() => setShowForm(false)}>Abbrechen</button>
          </div>
        </div>
      )}

      {/* Tabelle */}
      {loading ? (
        <div className="tbe-hint" style={{ padding: '1rem' }}>Lade…</div>
      ) : codes.length === 0 ? (
        <div className="tbe-hint-empty">Noch keine Rabattcodes für dieses Turnier.</div>
      ) : (
        <div className="tbe-table-wrap">
          <table className="tbe-codes-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Bezeichnung</th>
                <th>Rabatt</th>
                <th>Gültig für</th>
                <th>Nutzungen</th>
                <th>Aktiv</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {codes.map(c => (
                <tr key={c.id} style={{ opacity: c.aktiv ? 1 : 0.5 }}>
                  <td><span className="tbe-code-chip">{c.code}</span></td>
                  <td style={{ color: 'rgba(255,255,255,0.45)' }}>{c.bezeichnung || '—'}</td>
                  <td><strong>{formatRabatt(c)}</strong></td>
                  <td style={{ fontSize: '0.78rem' }}>{formatGueltigFuer(c)}</td>
                  <td style={{ fontSize: '0.78rem' }}>
                    {c.nutzungen}{c.max_nutzungen ? ` / ${c.max_nutzungen}` : ''}
                  </td>
                  <td>
                    <button
                      type="button"
                      className={`tbe-toggle-btn ${c.aktiv ? 'tbe-toggle-btn--on' : 'tbe-toggle-btn--off'}`}
                      onClick={() => handleToggle(c.id)}
                    >
                      {c.aktiv ? 'Ja' : 'Nein'}
                    </button>
                  </td>
                  <td style={{ display: 'flex', gap: '0.3rem' }}>
                    <button type="button" className="tbe-btn-icon" onClick={() => openEdit(c)} title="Bearbeiten">✎</button>
                    <button type="button" className="tbe-btn-icon tbe-btn-icon--del" onClick={() => handleDelete(c.id)} title="Löschen">✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
